import { P2GCheckBoxLegacy, P2GTextBox, P2GDropdown, P2GText } from '@parcel2go/core-ui';
import { useEffect, useMemo } from 'react';
import { ActionType, IntergrationInstructionInput } from '../../../lib/api/account-linking/types';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';

interface props {
  input: IntergrationInstructionInput;
  value: string;
  disabled: boolean;
  onUpdate: (key: string, value: string) => void;
  label?: React.ReactNode;
  integrationType: string;
  integrationSlug: string;
  action: ActionType;
}

export default function StepInput(props: props) {
  const { t } = useTranslation(TranslationNamespaces.ACCOUNT_LINKING);
  const onUpdate = (val: string) => props.onUpdate(props.input.propertyName, val);
  const label = props.input.placeholder + (props.input.validationRules.required ? ' *' : '');

  // Combine options into one list
  const options = useMemo(() => {
    const output: { value: string; text: string }[] = [];
    props.input.optionGroups?.forEach((element) => {
      element?.options?.forEach((o) => {
        output.push({ value: o.key, text: o.value });
      });
    });

    return output;
  }, [props.input]);

  // Set a default value if we have options
  useEffect(() => {
    if (!props.value && props.input.type === 'MultipleChoice' && options.length > 0) {
      props.onUpdate(props.input.propertyName, options[0].value);
    }
    if (!props.value && props.input.type === 'TrueOrFalse') {
      props.onUpdate(props.input.propertyName, 'false');
    }
  }, [props.value, props.input.type]);

  // Swap display based on input type
  switch (props.input.type) {
    case 'MultipleChoice':
      return (
        <P2GDropdown
          disabled={props.disabled}
          fullWidth
          label={t(`${props.integrationSlug}_${props.action}_${props.input.propertyName}_INPUT_LABEL`.toUpperCase(), {
            defaultValue: label
          })}
          id={props.input.propertyName}
          value={props.value}
          options={options}
          onChange={onUpdate}
        />
      );

    case 'TrueOrFalse':
      return (
        <P2GCheckBoxLegacy
          disabled={props.disabled}
          label={props.label || <P2GText variant="body2">{label}</P2GText>}
          id={props.input.propertyName}
          checked={props.value?.toLowerCase() === 'true'}
          onChange={(v) => onUpdate(v ? 'true' : 'false')}
        />
      );

    default:
      return (
        <P2GTextBox
          disabled={props.disabled}
          fullWidth
          label={t(`${props.integrationSlug}_${props.action}_${props.input.propertyName}_INPUT_LABEL`.toUpperCase(), {
            defaultValue: label
          })}
          id={props.input.propertyName}
          value={props.value}
          onChange={onUpdate}
          customError={props.value ? props.input.validationRules.helperText : null}
          pattern={props.input.validationRules.pattern ? new RegExp(props.input.validationRules.pattern) : undefined}
          showErrorsWhenPristine={true}
        />
      );
  }
}
